import { useCallback } from "react";

import { Box, Grid, SelectChangeEvent } from "@mui/material";
import {
  AvailablePaymentCurrency,
  formatCurrencyAmount,
  getDollarValue,
  hasGermanTipSuggestion,
  OrganizationCountry,
} from "@simplyk/common";
import { FieldValues } from "react-hook-form";

import { FormAmountField } from "../FormAmountField";

import { useLocaleContext } from "@/common/contexts/LocaleContext";
import { useTranslate } from "@/common/hooks/useTranslate";
import { TipSuggestionOutput } from "@/common/types/trpc";
// eslint-disable-next-line zeffy-linter-frontend/no-cross-layer-imports -- legacy cross-layer import (pre-rule); migrate when touched
import { SelectedTip } from "@/components/PaymentProcessor/hooks/useComputeTip";
import { FormSelectField, FormSelectFieldProps, FormSelectItem } from "@/forms-design-system/FormSelectField";

const WITHOUT_CENTS_OPTIONS = { minimumFractionDigits: 0, maximumFractionDigits: 0 };

export type FormPaymentTipSelectProps = Omit<FormSelectFieldProps<FieldValues>, "fullWidth" | "value" | "onChange"> & {
  currency: AvailablePaymentCurrency;
  options: TipSuggestionOutput[] | undefined;
  value: SelectedTip;
  onChange: (event: SelectChangeEvent<unknown>) => void;
  onChangeFreeTips: (event: React.ChangeEvent<HTMLInputElement>) => void;
  freeTips: number | "";
  onBlurTip: (event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => void;
  freeTipInputRef?: React.RefObject<HTMLInputElement | null>;
  isAuction: boolean;
  isSuggestedTipsAmount: boolean;
  organizationCountry?: OrganizationCountry;
};

export const FormPaymentTipSelect = ({
  currency,
  options,
  defaultValue,
  value,
  onChange,
  onChangeFreeTips,
  freeTips,
  onBlurTip,
  freeTipInputRef,
  isAuction,
  isSuggestedTipsAmount,
  organizationCountry,
  ...rest
}: FormPaymentTipSelectProps) => {
  const { t } = useTranslate();
  const { isoLocale } = useLocaleContext();

  const amountFormatOptions =
    isSuggestedTipsAmount &&
    organizationCountry &&
    hasGermanTipSuggestion(organizationCountry) &&
    options?.every(({ amount }) => amount % 100 === 0)
      ? WITHOUT_CENTS_OPTIONS
      : undefined;

  const renderOptionLabel = useCallback(
    (suggestedTip: TipSuggestionOutput) => {
      if (suggestedTip.amount === 0 && currency) {
        return formatCurrencyAmount({
          value: 0,
          locale: isoLocale,
          currency,
          options: amountFormatOptions,
        });
      }

      if ((!suggestedTip.percentage || isSuggestedTipsAmount) && currency && suggestedTip.amount !== 0) {
        return formatCurrencyAmount({
          value: getDollarValue(suggestedTip.amount),
          locale: isoLocale,
          currency,
          options: amountFormatOptions,
        });
      }

      if (suggestedTip.percentage && currency && suggestedTip.amount !== 0 && !isSuggestedTipsAmount) {
        const formattedPercent = new Intl.NumberFormat(isoLocale, {
          style: "percent",
          minimumFractionDigits: 0,
          maximumFractionDigits: 2,
        }).format(suggestedTip.percentage / 100);
        return `${formattedPercent}${
          isAuction
            ? ""
            : ` (${formatCurrencyAmount({ value: getDollarValue(suggestedTip.amount), locale: isoLocale, currency })})`
        }`;
      }

      return "";
    },
    [amountFormatOptions, currency, isAuction, isSuggestedTipsAmount, isoLocale]
  );

  return (
    <Grid container id="tip-section-input">
      <Grid item xs={12}>
        <FormSelectField
          defaultValue={defaultValue}
          value={value.isFree ? "free" : value.percentage?.toString()}
          data-test="tip-menu-item-select"
          aria-label={t("donationForm", "tip")}
          onChange={onChange}
          renderValue={(value) => {
            if (value === "free") {
              return t("donationForm", "freeTips");
            }

            const selectedTip = options?.find((tip) => tip.percentage?.toString() === value);
            if (!selectedTip) {
              return value;
            }
            return renderOptionLabel(selectedTip);
          }}
          fullWidth
          {...rest}
        >
          {options?.map((o) => (
            <FormSelectItem key={o.percentage} value={o.percentage} data-test="tip-menu-item">
              {renderOptionLabel(o)}
            </FormSelectItem>
          ))}
          <FormSelectItem key="free" value="free" data-test="free-tips-menu-item">
            {t("donationForm", "freeTips")}
          </FormSelectItem>
        </FormSelectField>

        {value.isFree && (
          <Box pt={2}>
            <FormAmountField
              value={freeTips === "" ? null : Math.round(freeTips * 100)}
              label={t("donationForm", "tip")}
              currency={currency}
              onChange={(centsValue) =>
                onChangeFreeTips({
                  target: { value: centsValue === null ? "" : String(getDollarValue(centsValue)) },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              onBlur={() =>
                onBlurTip({
                  target: { value: freeTips === "" ? "" : String(freeTips) },
                } as React.ChangeEvent<HTMLInputElement>)
              }
              error={typeof freeTips === "number" && freeTips < 0}
              helperText={
                typeof freeTips === "number" && freeTips < 0 ? t("donationForm", "tipsIsGreaterThanZero") : undefined
              }
              inputRef={freeTipInputRef}
              data-test="manual-contribution-input"
            />
          </Box>
        )}
      </Grid>
    </Grid>
  );
};
